/* © CNES */
import moment from 'moment';
import { Extent } from 'ol/extent';
import { apiConfiguration } from '../../api/APIConfiguration';
import {
  allVariableOptions,
  productTypeOptions,
} from '../../assets/config/filters/PostelReferenciel';
import {
  filterSections,
  Missions,
  productTypesNames,
  refDataProduct,
  refMissions,
  refPlatforms,
  RefResultsSatelliteName,
  RefSatelliteNames,
  refSensorModes,
  SatelliteNameType,
} from '../../assets/config/filters/Referenciel';
import { CartProduct } from '../../models/CartProduct';
import { CollectionDetails } from '../../models/CollectionDetails';
import { Feature } from '../../models/Feature';
import { ItemFilter } from '../../models/Filter';
import { ProductsAdded } from '../../models/ProductsAdded';
import { SelectButtonItem } from '../../models/SelectButtonItem';
import {
  ACQUISITION_STATION,
  BANDS,
  CLASSIFICATION,
  CLOUD_COVER,
  COUPLING_MODES,
  DATASET,
  DATETIME,
  END_DATE_TIME,
  END_POINT_URL,
  EO_SNOW_COVER,
  GERALD_NAME,
  GRID_CODE,
  IDENTIFIER,
  INSTRUMENT,
  INSTRUMENT_MODE,
  MISSION,
  MISSION_TAKE_ID,
  ORBIT_ABSOLUTE,
  ORBIT_RELATIVE,
  ORBIT_STATE,
  PARAMETER,
  PLATFORM,
  POLARIZATIONS,
  PPARAMETER,
  PROCESSING_CONTEXT,
  PROCESSING_CORRECTION,
  PROCESSING_DATETIME,
  PROCESSING_FACILITY,
  PROCESSING_LEVEL,
  PROCESSING_LINEAGE,
  PROCESSING_VERSION,
  PROCESSING_WEATHERDATA,
  PRODUCT_ACQUISITION_TYPE,
  PRODUCT_TIMELINESS,
  PRODUCT_TIMELINESS_CATEGORY,
  PRODUCT_TYPE,
  PRODUCT_VALIDITY,
  RASTER_SPATIAL_RESOLUTION,
  RESOLUTION_MODE,
  S2_DATASTRIP_ID,
  S2_DATATAKE_ID,
  S2_DATATAKE_TYPE,
  SAR_BEAM_IDS,
  SAR_CENTER_FREQUENCY,
  SAR_FREQUENCY_BAND,
  SAR_LOOKS_AZIMUTH,
  SAR_LOOKS_EQUIVALENT_NUMBER,
  SAR_LOOKS_RANGE,
  SAR_OBSERVATION_DIRECTION,
  SAR_PIXEL_SPACING_AZIMUTH,
  SAR_PIXEL_SPACING_RANGE,
  SAR_RELATIVE_BURST,
  SAR_RESOLUTION_AZIMUTH,
  SAR_RESOLUTION_RANGE,
  SENSOR_ANGLE,
  SENSOR_PITCH,
  SENSOR_ROLL,
  START_DATE_TIME,
  SWATH,
  SWATH_MODE,
  TEMPORAL_RESOLUTION,
  THEMES,
  VERSION,
  VIEW_AZIMUTH,
  VIEW_INCIDENCE_ANGLE,
  VIEW_OFF_NADIR,
  VIEW_SUN_AZIMUTH,
  VIEW_SUN_ELEVATION,
  VISIBILITY,
  WATER_COVER,
} from './Constants';

/**
 * transform Filter Data to format request to regards depending on key type.
 * @param key string
 * @param value string | number[]
 * @returns object
 */
export const transformFilterData = (
  key: string,
  value: string | number[],
  filterData: ItemFilter
): ItemFilter => {
  let transformedData: ItemFilter;
  switch (key) {
    case filterSections.mission.param:
      transformedData = {
        [key]: convertValuesToKeys(refMissions, value),
        [PPARAMETER]: null,
      };
      if (
        value &&
        refMissions.find((el) => (value as [number]).includes(el.value))
          ?.value === Missions.GEOV2_AVHRR
      ) {
        transformedData = {
          ...transformedData,
          [INSTRUMENT]: ['AVHRR'],
        };
      } else {
        transformedData = {
          ...transformedData,
          [INSTRUMENT]: null,
        };
      }
      break;
    case filterSections.platform.param:
      transformedData = { [key]: convertValuesToKeys(refPlatforms, value) };
      break;
    case filterSections.dataProduct.param:
      const formattedValue = convertValuesToKeys(refDataProduct, value);
      transformedData = convertDataProductToValues(formattedValue);
      break;
    case filterSections.sensorMode.param:
      transformedData = { [key]: convertValuesToKeys(refSensorModes, value) };
      break;
    case filterSections.productType.param:
      transformedData = {
        [key]: convertValuesToKeys(productTypeOptions, value),
      };
      // update values of filter parameter when productType change
      if (filterData[PPARAMETER] && Array.isArray(filterData[PPARAMETER])) {
        const filteredVariableOptions: string[] = Array.isArray(value)
          ? allVariableOptions
              .filter((variableItem) => {
                if (Array.isArray(variableItem.productTypeId)) {
                  return variableItem.productTypeId.some((id) =>
                    value.includes(id)
                  );
                } else {
                  return value.includes(variableItem.productTypeId);
                }
              })
              .map(({ key }) => key)
          : [];
        transformedData = {
          [key]: convertValuesToKeys(productTypeOptions, value),
          [PPARAMETER]: filterData[PPARAMETER]?.filter((el) =>
            filteredVariableOptions.includes(el)
          ),
        };
      }
      break;
    case filterSections.variable.param:
      transformedData = {
        [key]: convertValuesToKeys(allVariableOptions, value),
      };
      break;
    case filterSections.cloudCover.param:
      transformedData = { [key]: [value[0].toString(), value[1].toString()] };
      break;
    case filterSections.whereAbsoluteOrbit.param:
      transformedData = {
        [key]: value ? (value as string).split(',') : null,
        [ORBIT_RELATIVE]: null,
      };
      break;
    case filterSections.whereRelativeOrbit.param:
      transformedData = {
        [key]: value ? (value as string).split(',') : null,
        [ORBIT_ABSOLUTE]: null,
      };
      break;
    case filterSections.whereTile.param:
      transformedData = {
        [key]: value ? (value as string) : null,
      };
      break;
    default:
      transformedData = { [key]: value.toString() };
      break;
  }
  return transformedData;
};

/**
 * convert Values To Keys.
 * @param refData SelectButtonItem[]
 * @param value string | number[]
 * @returns string[] | null
 */
export const convertValuesToKeys = (
  refData: SelectButtonItem[],
  value: string | number | number[]
) => {
  if (Array.isArray(value) && value.length) {
    return refData
      .filter((el) => value.includes(el.value))
      .map((el) => el.key as string[])
      .flat();
  }
  return null;
};

/**
 * transform filter context data to url string
 * @param filter filter data
 * @param drawnShape drawnshape
 * @param bbox bbox data
 * @returns url string
 */
export const parseFilterContextToUrl = (
  filter: ItemFilter,
  drawnShape?: Extent | undefined,
  bbox?: number[]
) => {
  let url = '';
  const filterKeys = Object.keys(filter).filter(
    (key) => filter[key as keyof typeof filter] && filter[key]?.length
  );

  filterKeys.forEach((el) => {
    switch (el) {
      case DATASET:
        {
          const dataTypes = filter[el];
          const missionFilter: string[] = [];
          if (Array.isArray(dataTypes)) {
            const refS1 = refMissions.find(
              (refMission) => refMission.name === 'Sentinel 1'
            );
            const refS2 = refMissions.find(
              (refMission) => refMission.name === 'Sentinel 2'
            );
            // handle the case when it is a POSTEL or GEOV2 product
            if (dataTypes.find((el) => el.includes('POSTEL'))) {
              const refMission = refMissions.find((refMission) => {
                return (
                  JSON.stringify(refMission.key) === JSON.stringify(dataTypes)
                );
              });
              missionFilter.push(
                refMission?.value === Missions.GEOV2_AVHRR
                  ? 'GEOV2-AVHRR'
                  : refMission?.name!
              );
            } else {
              dataTypes?.forEach((dataType: string) => {
                if (
                  refS1?.key.includes(dataType) &&
                  !missionFilter.includes('Sentinel-1')
                ) {
                  missionFilter.push('Sentinel-1');
                } else if (
                  refS2?.key.includes(dataType) &&
                  !missionFilter.includes('Sentinel-2')
                ) {
                  missionFilter.push('Sentinel-2');
                } else {
                  const refMission = refMissions.find((refMission) => {
                    return (
                      refMission.key.includes(dataType) &&
                      !['Sentinel 1', 'Sentinel 2'].includes(refMission.name)
                    );
                  });
                  if (refMission && !missionFilter.includes(refMission?.name)) {
                    missionFilter.push(refMission?.name);
                  }
                }
              });
            }
          }
          url += 'dataType=' + missionFilter + '&';
        }
        break;
      case END_DATE_TIME:
        url +=
          'startDate=' +
          moment(filter[el]?.slice(0, -1)).format('YYYY-MM-DDTHH:mm') +
          '&';
        break;
      case START_DATE_TIME:
        url +=
          'endDate=' +
          moment(filter[el]?.slice(0, -1)).format('YYYY-MM-DDTHH:mm') +
          '&';
        break;
      case CLOUD_COVER:
        url += 'cloudCover=[' + filter[el]![0] + ',' + filter[el]![1] + ']&';
        break;
      case PLATFORM:
        url += 'satellitePlatform=' + filter[el] + '&';
        break;
      case 'dataProduct':
        url += 'dataProduct=' + filter[el] + '&';
        break;
      case INSTRUMENT_MODE:
        url += 'sensorMode=' + filter[el] + '&';
        break;
      case PRODUCT_TYPE:
        const productType =
          filter[el] === 'Multispectral (XS)'
            ? 'Multispectral%20(XS)'
            : filter[el];
        url += 'productType=' + productType + '&';
        break;
      case PPARAMETER:
        url += 'Variable=' + filter[el] + '&';
        break;
      case IDENTIFIER:
        url += 'endpointURL=' + filter[el] + '&';
        break;
      case ORBIT_ABSOLUTE:
        url += 'absoluteOrbitID=' + filter[el] + '&';
        break;
      case ORBIT_RELATIVE:
        url += 'orbitID=' + filter[el] + '&';
        break;
      case GRID_CODE:
        url += 'TileID=' + filter[el] + '&';
        break;
    }
  });

  if (drawnShape) {
    url += 'handDrawnArea=' + drawnShape + '&';
  }

  if (bbox) {
    url += 'box=' + bbox;
  } else {
    url = url.slice(0, -1);
  }
  return url;
};

/**
 * transform url params to context filter data
 * @param urlParams list of params from url
 * @returns contextFilter and bbox
 */
export const parseUrlToContextFilter = (
  urlParams: { key: string; value: string }[]
) => {
  let contextFilter: ItemFilter = {
    [ORBIT_ABSOLUTE]: null,
    [ORBIT_RELATIVE]: null,
    [GRID_CODE]: null,
    [DATASET]: null,
    dataProduct: null,
    [PLATFORM]: null,
    [PRODUCT_TYPE]: null,
    [PROCESSING_LEVEL]: null,
    [INSTRUMENT_MODE]: null,
    [CLOUD_COVER]: null,
    [START_DATE_TIME]: null,
    [END_DATE_TIME]: null,
  };
  const selectedMissions: number[] | null = [];
  let selectedPostelProductType: number[] | null = [];
  let bbox: number[] | null = null;
  let drawnShape: number[] | null = null;
  const refS1 = refMissions.find(
    (refMission) => refMission.name === 'Sentinel 1'
  );
  const refS2 = refMissions.find(
    (refMission) => refMission.name === 'Sentinel 2'
  );
  urlParams.forEach(({ key, value }) => {
    switch (key) {
      case 'dataType':
        {
          const dataType: string[] = [];
          value.split(',').forEach((el) => {
            if (el === 'Sentinel-1') {
              refS1?.key.forEach((ref) => {
                dataType.push(ref);
              });
              selectedMissions.push(Missions.sentinel1);
            }
            if (el === 'Sentinel-2') {
              refS2?.key.forEach((ref) => {
                dataType.push(ref);
              });
              selectedMissions.push(Missions.sentinel2);
            }
            if (el === 'GEOV2-AVHRR') {
              const refGEOV = refMissions.find(
                (el) => el.value === Missions.GEOV2_AVHRR
              );
              refGEOV?.key.forEach((el) => {
                dataType.push(el);
              });
              if (refGEOV) {
                selectedMissions.push(refGEOV?.value);
              }
            } else {
              const refMissionInUrl = refMissions.find(
                (refMission) => refMission.name === el
              );
              refMissionInUrl?.key.forEach((ref) => {
                dataType.push(ref);
              });
              if (refMissionInUrl) {
                selectedMissions.push(refMissionInUrl?.value);
              }
            }
          });
          contextFilter = { ...contextFilter, [DATASET]: dataType };
        }
        break;
      case 'satellitePlatform':
        contextFilter = {
          ...contextFilter,
          [PLATFORM]: value.split(','),
        };
        break;
      case 'Variable':
        contextFilter = {
          ...contextFilter,
          [PPARAMETER]: value.split(','),
        };
        break;
      case 'productType':
        selectedPostelProductType = productTypeOptions
          .filter((productType) => value.split(',').includes(productType.name))
          .map(({ value }) => value);

        const productTypeTable = value.split(',').map((item) => {
          if (item === 'Multispectral%20(XS)') {
            return 'Multispectral (XS)';
          }
          return item;
        });

        contextFilter = {
          ...contextFilter,
          [PRODUCT_TYPE]: productTypeTable,
        };
        break;
      case 'dataProduct':
        const dataProductTable = value.split(',');
        const productLevelTable: string[] = [];
        const productTypeTableFromDataProduct: string[] = [];

        dataProductTable.forEach((dataProduct) => {
          const urlDataProduct = refDataProduct.find((refDataProduct) => {
            return refDataProduct.key === dataProduct;
          });

          if (urlDataProduct?.type === 'productLevel') {
            productLevelTable.push(urlDataProduct.key);
            urlDataProduct.relation.forEach((relation) =>
              productTypeTableFromDataProduct.push(relation)
            );
          } else if (urlDataProduct?.type === 'productType') {
            productTypeTableFromDataProduct.push(urlDataProduct.key);
            urlDataProduct.relation.forEach((relation) =>
              productLevelTable.push(relation)
            );
          }
        });
        contextFilter = {
          ...contextFilter,
          dataProduct: dataProductTable,
          [PRODUCT_TYPE]: productTypeTableFromDataProduct,
          [PROCESSING_LEVEL]: productLevelTable,
        };
        break;
      case 'sensorMode':
        contextFilter = {
          ...contextFilter,
          [INSTRUMENT_MODE]: value.split(','),
        };
        break;
      case 'startDate':
        contextFilter = {
          ...contextFilter,
          [END_DATE_TIME]: moment(value).utcOffset(0, true).toISOString(),
        };
        break;
      case 'endDate':
        contextFilter = {
          ...contextFilter,
          [START_DATE_TIME]: moment(value).utcOffset(0, true).toISOString(),
        };
        break;
      case 'cloudCover':
        contextFilter = {
          ...contextFilter,
          [CLOUD_COVER]: JSON.parse(value),
        };
        break;
      case 'box':
        bbox = value.split(',').map(Number);
        break;
      case 'endpointURL':
        contextFilter = {
          ...contextFilter,
          [IDENTIFIER]: value,
        };
        break;
      case 'absoluteOrbitID':
        contextFilter = {
          ...contextFilter,
          [ORBIT_ABSOLUTE]: value.split(','),
        };
        break;
      case 'orbitID':
        contextFilter = {
          ...contextFilter,
          [ORBIT_RELATIVE]: value.split(','),
        };
        break;
      case 'handDrawnArea':
        drawnShape = value.split(',').map(Number);
        break;
      case 'TileID':
        contextFilter = {
          ...contextFilter,
          [GRID_CODE]: value,
        };
        break;
    }
  });
  return {
    contextFilter,
    selectedMissions: selectedMissions.length ? selectedMissions : null,
    selectedPostelProductType,
    bbox,
    drawnShape,
  };
};

/**
 * function to get item options for sub section of mission filter (platform,product type, sensor mode)
 * @param missions list of mission values
 * @returns options of platform, product type and sensor mode
 */
export const getOptionsForSubSections = (
  missions: string | number[] | null | undefined
) => {
  let filteredPlatformOptions: SelectButtonItem[] = [];
  let filteredProductTypesOptions: SelectButtonItem[] = [];
  let filteredProductLevelOptions: SelectButtonItem[] = [];
  let filteredSensorModeOptions: SelectButtonItem[] = [];

  if (missions === 'All') {
    return {
      filteredPlatformOptions: refPlatforms,
      filteredProductTypesOptions: refDataProduct,
      filteredProductLevelOptions: refDataProduct.filter(
        (dataProduct) => dataProduct.type === 'productLevel'
      ),
      filteredSensorModeOptions: refSensorModes,
    };
  }

  if (Array.isArray(missions)) {
    const missionSet = new Set(missions);

    filteredPlatformOptions = refPlatforms.filter((el) =>
      el.missionId.some((missionId: number) => missionSet.has(missionId))
    );

    refDataProduct.forEach((dataProduct) => {
      const isMissionMatched = dataProduct.missionId.some((missionId: number) =>
        missionSet.has(missionId)
      );

      if (isMissionMatched) {
        filteredProductTypesOptions.push(dataProduct);
        if (dataProduct.type === 'productLevel') {
          filteredProductLevelOptions.push(dataProduct);
        }
      }
    });

    filteredSensorModeOptions = refSensorModes.filter((el) =>
      missionSet.has(el.missionId)
    );
  }

  return {
    filteredPlatformOptions,
    filteredProductTypesOptions,
    filteredProductLevelOptions,
    filteredSensorModeOptions,
  };
};

/**
 * get default values for mission filter
 * @param dataType values of data type in filter context
 * @returns list of mission value
 */
export const getDefaultSelectedOption = (
  dataType: string | string[] | null,
  refOptions: SelectButtonItem[]
): number[] | null => {
  if (!dataType) {
    return null;
  }
  const dataSet = Array.isArray(dataType) ? new Set(dataType) : null;
  return refOptions.reduce<number[]>((acc, option) => {
    if (
      dataSet &&
      Array.isArray(option.key) &&
      option.key.some((el) => dataSet.has(el))
    ) {
      acc.push(option.value);
    }
    return acc;
  }, []);
};

/**
 * get ProductsAdded object from data Cart API
 * @param productData data api
 * @returns ProductsAdded context type object
 */
export const getProductsAddedObject = (productData: CartProduct[]) => {
  let productsName: string[] = [];
  let productDetail: CollectionDetails;

  let productCollectionDetail: CollectionDetails[] = [];
  productData.forEach((cartProduct: CartProduct) => {
    productsName.push(cartProduct.name);
    productDetail = {
      ...productDetail,
      id: cartProduct.urn,
      checksum: cartProduct.checksum,
      productName: cartProduct.name,
      quicklook: cartProduct.quicklook,
      endpointURL: cartProduct.endpointURL,
      href: cartProduct.href,
      assets: cartProduct.assets,
      collectionDataModel: {
        general: {
          collection: '',
          sensingStart: '',
          sensingEnd: '',
          storageMode: null,
          totalSize: 0,
        },
        productDetails: {
          [PLATFORM]: null,
          [PARAMETER]: null,
          [PPARAMETER]: null,
          [PROCESSING_LEVEL]: null,
          [GRID_CODE]: null,
          [PRODUCT_TYPE]: null,
          [CLOUD_COVER]: null,
          [EO_SNOW_COVER]: null,
          [WATER_COVER]: null,
          [ORBIT_STATE]: null,
          [ORBIT_ABSOLUTE]: null,
          [ORBIT_RELATIVE]: null,
          [BANDS]: null,
          [S2_DATATAKE_ID]: null,
          [S2_DATATAKE_TYPE]: null,
          [S2_DATASTRIP_ID]: null,
          [VIEW_INCIDENCE_ANGLE]: null,
          [VIEW_AZIMUTH]: null,
          [VIEW_SUN_AZIMUTH]: null,
          [VIEW_SUN_ELEVATION]: null,
          [VIEW_OFF_NADIR]: null,
          [SWATH]: null,
          [SWATH_MODE]: null,
          [DATETIME]: null,
          [PRODUCT_TIMELINESS]: null,
          [PRODUCT_TIMELINESS_CATEGORY]: null,
          [PRODUCT_ACQUISITION_TYPE]: null,
          [PROCESSING_CONTEXT]: null,
          [PROCESSING_CORRECTION]: null,
          [PROCESSING_DATETIME]: null,
          [PROCESSING_LINEAGE]: null,
          [PROCESSING_VERSION]: null,
          [INSTRUMENT]: null,
          [INSTRUMENT_MODE]: null,
          [POLARIZATIONS]: null,
          [COUPLING_MODES]: null,
          [SAR_FREQUENCY_BAND]: null,
          [SAR_CENTER_FREQUENCY]: null,
          [SAR_RESOLUTION_RANGE]: null,
          [SAR_RESOLUTION_AZIMUTH]: null,
          [SAR_PIXEL_SPACING_RANGE]: null,
          [SAR_PIXEL_SPACING_AZIMUTH]: null,
          [SAR_LOOKS_RANGE]: null,
          [SAR_LOOKS_AZIMUTH]: null,
          [SAR_LOOKS_EQUIVALENT_NUMBER]: null,
          [SAR_OBSERVATION_DIRECTION]: null,
          [SAR_RELATIVE_BURST]: null,
          [SAR_BEAM_IDS]: null,
          [SENSOR_ANGLE]: null,
          [SENSOR_PITCH]: null,
          [SENSOR_ROLL]: null,
          [RASTER_SPATIAL_RESOLUTION]: null,
          [TEMPORAL_RESOLUTION]: null,
          [RESOLUTION_MODE]: null,
          [MISSION_TAKE_ID]: null,
          [GERALD_NAME]: null,
          [ACQUISITION_STATION]: null,
          [CLASSIFICATION]: null,
          [THEMES]: null,
          [PROCESSING_FACILITY]: null,
          [MISSION]: null,
          [PROCESSING_WEATHERDATA]: null,
          [VERSION]: null,
          [PRODUCT_VALIDITY]: null,
          [END_POINT_URL]: null,
          [DATASET]: cartProduct.mission,
          [VISIBILITY]: null,
          [START_DATE_TIME]: null,
          [END_DATE_TIME]: null,
        },
        geometry: { multipolygon: undefined },
        keywords: [''],
        location: {
          continents: [
            {
              name: '',
              countries: [
                {
                  name: '',
                  regions: [
                    {
                      name: '',
                      states: [{ name: '' }],
                    },
                  ],
                },
              ],
            },
          ],
        },
      },
    };
    productCollectionDetail.push(productDetail);
  });
  return {
    products: productsName,
    results: productsName,
    levelSet: new Set(),
    satelliteSet: new Set(),
    collectionsDetails: productCollectionDetail,
  } as ProductsAdded;
};

/**
 * function to filter platform, product type and sensor mode values for context
 * @param missions list of missions values
 * @param filter context filter
 * @returns platform, product type and sensor mode values
 */
export const filterSubSectionsOfMission = (
  missions: string | number[] | null | undefined,
  filter: ItemFilter
) => {
  let filterPlatform = filter[PLATFORM];
  let filterProductType = filter[PRODUCT_TYPE];
  let filterProductLevel = filter[PROCESSING_LEVEL];
  let filterSensorMode = filter[INSTRUMENT_MODE];
  let filterDataProduct = filter['dataProduct'];
  const {
    filteredPlatformOptions,
    filteredProductTypesOptions,
    filteredProductLevelOptions,
    filteredSensorModeOptions,
  } = getOptionsForSubSections(missions);

  const getFilteredValues = (
    filter: string[] | string | null,
    options: SelectButtonItem[]
  ) => {
    if (filter instanceof Array) {
      const keysSet = new Set(options.map((item) => item.key));
      return filter.filter((el) => keysSet.has(el));
    }
    return filter;
  };
  filterPlatform = getFilteredValues(filterPlatform, filteredPlatformOptions);
  filterProductType = getFilteredValues(
    filterProductType,
    filteredProductTypesOptions
  );
  filterProductLevel = getFilteredValues(
    filterProductLevel,
    filteredProductLevelOptions
  );
  if (filterProductLevel && filterProductType) {
    filterDataProduct = (filterProductLevel as string[]).concat(
      filterProductType as string[]
    );
  }
  filterSensorMode = getFilteredValues(
    filterSensorMode,
    filteredSensorModeOptions
  );

  let cloudCover = filter[CLOUD_COVER];

  if (missions instanceof Array && !missions?.includes(Missions.sentinel2)) {
    cloudCover = null;
  }

  return {
    dataProduct: filterDataProduct,
    [PLATFORM]: filterPlatform,
    [PRODUCT_TYPE]: filterProductType,
    [PROCESSING_LEVEL]: filterProductLevel,
    [INSTRUMENT_MODE]: filterSensorMode,
    [CLOUD_COVER]: cloudCover,
  };
};

export const addKeyToFilterParam = (
  key: string,
  filterParamTable: string[]
) => {
  if (!filterParamTable.includes(key)) {
    filterParamTable.push(key);
  }
  return filterParamTable;
};

export const convertDataProductToValues = (
  dataProductList: string[] | null
) => {
  let newProductLevels: string[] = [];
  let newProductTypes: string[] = [];
  dataProductList?.forEach((dataProductKey) => {
    const dataProductSelected = refDataProduct.find((dataProduct) => {
      return dataProduct.key === dataProductKey;
    });

    if (dataProductSelected?.type === 'productType') {
      newProductTypes = addKeyToFilterParam(
        dataProductSelected.key,
        newProductTypes
      );

      dataProductSelected.relation.forEach((relation) => {
        newProductLevels = addKeyToFilterParam(relation, newProductLevels);
      });
    } else if (dataProductSelected?.type === 'productLevel') {
      const keyContainsSatelliteName = RefSatelliteNames.find((name) => {
        return dataProductSelected.key.includes(name);
      });

      if (keyContainsSatelliteName) {
        dataProductSelected.key = dataProductSelected.key.replace(
          keyContainsSatelliteName,
          ''
        );
      }

      newProductLevels = addKeyToFilterParam(
        dataProductSelected.key,
        newProductLevels
      );
      dataProductSelected.relation.forEach((relation) => {
        newProductTypes = addKeyToFilterParam(relation, newProductTypes);
      });
    }
  });

  return {
    dataProduct: dataProductList ?? [],
    [PRODUCT_TYPE]: newProductTypes,
    [PROCESSING_LEVEL]: newProductLevels,
  };
};

/**
 * Build Level Name for Results.
 * @param feature feature from backend.
 * @returns Displayed level on Results.
 */
export const buildLevelName = (feature: any) => {
  let levelName = '';
  if (
    feature.properties[PLATFORM]?.slice(0, -1) === 'S1' ||
    feature.properties[PRODUCT_TYPE] === 'Vegetation' ||
    feature.properties[PRODUCT_TYPE] === 'Radiation' ||
    feature.properties[PRODUCT_TYPE] === 'Water' ||
    feature.properties[PRODUCT_TYPE] === 'LandCover' ||
    feature.properties[PRODUCT_TYPE] === 'L2B_BIOPHY' ||
    feature.properties[PRODUCT_TYPE] === 'DEM' ||
    feature.properties[PRODUCT_TYPE] === 'IMREF'
  ) {
    levelName =
      productTypesNames[
        feature.properties[PRODUCT_TYPE] as keyof typeof productTypesNames
      ];
  } else {
    levelName =
      productTypesNames[
        feature.properties[PROCESSING_LEVEL] as keyof typeof productTypesNames
      ];
  }

  return levelName;
};

/**
 * Builds the satellite name based on a given `Feature` object and applied filters.
 *
 * @param {Feature} feature - The object containing platform and dataset information.
 * @param {ItemFilter} filterData - The applied filters, especially for special cases like AVHRR.
 * @returns {string} - The formatted satellite name, corresponding to a key in `RefResultsSatelliteName`.
 */
export const buildSatelliteName = (
  feature: Feature,
  filterData: ItemFilter
): string => {
  // Set of satellite names that should not be truncated
  const excludedNames = new Set([
    'MULTISAT',
    'OSO',
    'POSTEL',
    'SPOT4_TAKE5',
    'SPOT5_TAKE5',
    'TRISHNA',
  ]);

  // Determine the initial satellite name
  let collectionSatelliteName: SatelliteNameType =
    (feature.properties[PLATFORM] as SatelliteNameType) ||
    (feature.collection.split('_')[0] as SatelliteNameType);

  // Special case: If the AVHRR instrument is selected, force the name "GEOV2_AVHRR"
  if (filterData[INSTRUMENT]?.includes('AVHRR')) {
    return RefResultsSatelliteName['GEOV2_AVHRR'];
  }

  // Trim the last character from the satellite name unless it's in the exclusion list
  if (!excludedNames.has(collectionSatelliteName)) {
    collectionSatelliteName = collectionSatelliteName.slice(
      0,
      -1
    ) as SatelliteNameType;
  }

  // Special case: If the satellite is "SPOT" but belongs to the "SPIRIT" mission
  if (
    collectionSatelliteName === 'SPOT' &&
    feature.properties[DATASET] === 'THEIA_SPIRIT_SPOT5_L1A'
  ) {
    collectionSatelliteName = 'SPIRIT';
  }

  // Return the formatted satellite name
  return RefResultsSatelliteName[collectionSatelliteName];
};

/**
 * Replace Rs Catalog url string by new quicklook path from kong.
 * @param path the RS catalog path from items
 * @returns the quicklook path without rate limiting.
 */
export const replaceCatalogPathForQuicklook = (path: string) => {
  return path;
  /**
  return `${apiConfiguration.apiBaseUrl}/api${path
    ?.replace('/v1/rs-catalog/downloads/', '/quicklook/')
    .replace('/download/', '/quicklook/')}&isContentInline=true`;
   */
};

/**
 * check if input value is number
 * @param e event param
 * @returns boolean regex
 */
export const isNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
  const regex = /^[0-9,\b]+$/;
  // if value is not blank, then test the regex
  return e.target.value === '' || regex.test(e.target.value);
};

/**
 * check if input value is compatible with a Tile Identifier
 * @param value Tile value string to evaluate
 * @returns boolean regex
 */
export const isTile = (value: string) => {
  /**
   * Regex value without T (ex: 31TCJ):
   * fisrt and second characters are number, ,
   * 3 last characters are letters,
   * length 5,
   * no special characters
   */
  const regex_without_T = /^[0-9]{2}[A-Z]{3}$/;

  /**
   * Regex value with T (ex: T31TCJ):
   * first character is T,
   * second and third characters are number,
   * 3 last characters are letters,
   * length 6,
   * no special characters
   */
  const regex_with_T = /^T[0-9]{2}[A-Z]{3}$/;

  // if value is not blank, then test the regex
  return (
    value === '' || regex_without_T.test(value) || regex_with_T.test(value)
  );
};

/**
 * get tooltip not logged message
 * @param isUserNotLogged
 * @param isDownloadMessage
 * @returns
 */
export const getTooltipMessage = (
  isUserNotLogged: boolean,
  isDownloadMessage?: boolean,
  isDatalakeAvailable?: string
) => {
  if (isUserNotLogged) {
    return 'Please log in to use this feature';
  } else if (isDatalakeAvailable === 'unavailable') {
    return 'Datalake unavailable';
  } else if (isDatalakeAvailable === 'loading') {
    return 'Pending response...';
  } else if (isDownloadMessage) {
    return 'Download may take a few minutes to start';
  }

  return '';
};

/**
 * show cloud cover slider if combinaison of filter allows it.
 * @param missions satellite missions.
 * @param filterData context filter.
 * @returns
 */
export const showCloudCover = (
  missions: string | number[] | null | undefined,
  filterData: ItemFilter
) => {
  if (missions instanceof Array && missions.includes(Missions.sentinel2)) {
    return true;
  }
  if (missions instanceof Array && missions.includes(Missions.multisat)) {
    return true;
  }
  if (
    missions instanceof Array &&
    missions.includes(Missions.landsat) &&
    (filterData[PLATFORM]?.includes('LANDSAT8') ||
      filterData[PLATFORM] === null)
  ) {
    return true;
  }

  if (
    missions instanceof Array &&
    missions.includes(Missions.spot) &&
    (filterData['dataProduct']?.includes('L1C') ||
      filterData['dataProduct']?.includes('L2A') ||
      filterData['dataProduct'] === null ||
      filterData['dataProduct']?.length === 0)
  ) {
    return true;
  }

  if (
    missions instanceof Array &&
    missions.includes(Missions.venus) &&
    (filterData['dataProduct']?.includes('L1C') ||
      filterData['dataProduct']?.includes('L2A') ||
      filterData['dataProduct'] === null ||
      filterData['dataProduct']?.length === 0)
  ) {
    return true;
  }
};

/**
 * Checks if a specific item is available in the given list of items.
 *
 * @param items - The list of items to search through.
 * @param itemId - The ID of the item to check for availability.
 * @returns {boolean | undefined} - Returns `true` if the item is available, `false` if not available, or `undefined` if the item is not found.
 */
export const isItemAvailable = (
  items:
    | {
        productId: string;
        available: boolean | undefined;
      }[]
    | undefined,
  itemId: string
) => {
  return items?.find((item) => item.productId === itemId)?.available;
};
